import { HttpClient } from '@angular/common/http';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { skipErrorDialog } from './interceptors/error.interceptor';

export function MSALRedirectFactory(
  auth: MsalService,
  http: HttpClient,
  bc: MsalBroadcastService,
): () => Promise<boolean> {
  return async () => {
    try {
      // Handle any pending redirect from authentication
      await firstValueFrom(auth.handleRedirectObservable());

      // Wait for any in-progress interactions to complete
      await firstValueFrom(
        bc.inProgress$.pipe(
          filter((s) => s === InteractionStatus.None),
          take(1),
        ),
      );

      // Check if we have accounts after handling redirect
      const accounts = auth.instance.getAllAccounts();

      if (accounts.length > 0) {
        // Set the active account for subsequent operations
        auth.instance.setActiveAccount(accounts[0]);

        // Validate authentication with backend
        await firstValueFrom(http.get('/dummy/validation', { context: skipErrorDialog() })).catch(() => true);
        return true;
      }

      // No accounts found - initiate login redirect
      // loginRedirect navigates away, so we await it but it never actually completes
      // (the browser redirects to Azure AD before the observable emits)
      await firstValueFrom(auth.loginRedirect());

      // This line will never be reached in practice because redirect navigates away
      return false;
    } catch (error) {
      console.error('Error in MSAL initialization:', error);
      // On error, attempt login redirect as fallback
      try {
        await firstValueFrom(auth.loginRedirect());
      } catch (redirectError) {
        console.error('Failed to initiate login redirect:', redirectError);
      }
      return false;
    }
  };
}
